import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { Trophy, Flame, Crown, RefreshCw, Lock, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import TierBadge from "../components/TierBadge";

// Leaderboard — Phase 2 (Jun 2026):
//   • Three periods: Today / This week / All-time (Monthly retired)
//   • Multi-criterion ranking on the server: total_seconds → streak_days
//     → achievements_count
//   • "My Rank" card always visible (even when user is off-list)
//   • Free users see top-10 preview + upgrade nudge; premium sees full top-50
//   • Achievement count column reads from each user's earned-badge total
//   • Auto-refresh every 60s + manual refresh button. Cache lives server-side
//     (5-min TTL); the 60s tick rides the cache so it's essentially free.
const PERIODS = [
  { id: "daily",   label: "Today" },
  { id: "weekly",  label: "This week" },
  { id: "all",     label: "All-time" },
];

const AUTO_REFRESH_MS = 60_000;

const fmtDuration = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  if (m) return `${m}m`;
  return `0m`;
};

const RankMedal = ({ rank }) => {
  if (rank === 1) return <span aria-label="rank 1" className="text-base sm:text-lg">🥇</span>;
  if (rank === 2) return <span aria-label="rank 2" className="text-base sm:text-lg">🥈</span>;
  if (rank === 3) return <span aria-label="rank 3" className="text-base sm:text-lg">🥉</span>;
  if (rank == null) return <span className="text-xs text-zinc-400">—</span>;
  return <span className="text-xs font-semibold text-zinc-500">#{rank}</span>;
};

const Avatar = ({ src, name }) => {
  if (src) return <img src={src} alt={name} className="h-10 w-10 rounded-full object-cover"/>;
  return (
    <div className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500 text-sm font-semibold text-white">
      {(name?.[0] || "?").toUpperCase()}
    </div>
  );
};

export default function Leaderboard() {
  const { user } = useAuth();
  const { effectivePremium } = useAccess();
  const [period, setPeriod] = useState("weekly");
  const [data, setData] = useState({ rows: [], my_rank: null, total_participants: 0, is_premium: false, free_preview_size: 10 });
  const [meta, setMeta] = useState({ cached: false, computed_at: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data: resp } = await api.get(`/leaderboard?period=${p}`);
      setData({
        rows: resp.rows || [],
        my_rank: resp.my_rank,
        total_participants: resp.total_participants || 0,
        is_premium: !!resp.is_premium,
        free_preview_size: resp.free_preview_size || 10,
      });
      setMeta({ cached: resp.cached, computed_at: resp.computed_at });
    } finally { if (!silent) setLoading(false); }
  }, []);

  // Initial load (with skeleton) on mount + period change.
  useEffect(() => { load(period); }, [load, period]);

  // Auto-refresh every 60s. Silent: no skeleton flash, no rank jiggle —
  // we just swap the data in-place. Server-side cache (5-min TTL) makes
  // most ticks free; only the first tick after cache expiry actually
  // recomputes. Pauses when the tab is hidden (saves bandwidth) and
  // resumes on visibility-change.
  useEffect(() => {
    let timer;
    const start = () => {
      stop();
      timer = setInterval(() => {
        if (!document.hidden) load(period, { silent: true });
      }, AUTO_REFRESH_MS);
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = undefined; } };
    const onVis = () => { if (!document.hidden) { load(period, { silent: true }); start(); } else { stop(); } };
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [load, period]);

  // Find a row in the visible list matching the current user — used to
  // overlay a "You" badge on the in-list row.
  const myRowIndex = useMemo(
    () => data.rows.findIndex((r) => r.user_id === user?.user_id),
    [data.rows, user]
  );

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <Trophy size={18}/>
            </div>
            <h1 className="font-heading text-2xl font-bold sm:text-3xl">Leaderboard</h1>
          </div>
          <p className="mt-1 text-sm text-zinc-500">Top aspirants by study time. Refreshes automatically every minute.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-x-auto rounded-lg border border-zinc-200 bg-white p-0.5 dark:border-zinc-800 dark:bg-zinc-900" data-testid="leaderboard-period-toggle">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition ${period === p.id ? "bg-emerald-500 text-white" : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"}`}
                data-testid={`leaderboard-period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="icon" onClick={() => load(period, { silent: true })} aria-label="Refresh" data-testid="leaderboard-refresh">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""}/>
          </Button>
        </div>
      </header>

      {/* My Rank card — visible to EVERY user. Tells the story: "you are #X of Y" */}
      {data.my_rank && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-50 p-4 dark:bg-emerald-500/10" data-testid="leaderboard-my-rank">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Your rank</div>
              <div className="mt-1.5 flex items-baseline gap-2">
                <span className="font-heading text-3xl font-bold tabular-nums">
                  {data.my_rank.rank ? `#${data.my_rank.rank}` : "—"}
                </span>
                {data.total_participants > 0 && (
                  <span className="text-sm text-zinc-500">of {data.total_participants}</span>
                )}
                {/* Tier badge on the user's own rank card — same source as
                    Header / Profile / Community. Shown for premium & trial;
                    Free users see the compact free chip. */}
                {data.my_rank.tier && (
                  <TierBadge tier={data.my_rank.tier} size="xs" showFree testid="my-rank-tier" />
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-right">
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500">Time</div>
                <div className="text-sm font-semibold tabular-nums">{fmtDuration(data.my_rank.total_seconds || 0)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500">Streak</div>
                <div className="text-sm font-semibold inline-flex items-center gap-1" data-testid="my-rank-streak">
                  <Flame size={12} className="text-amber-500"/>
                  {data.my_rank.streak_days > 0 ? `${data.my_rank.streak_days}d` : "—"}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500">Badges</div>
                <div className="text-sm font-semibold inline-flex items-center gap-1"><Award size={12} className="text-emerald-500"/>{data.my_rank.achievements_count}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        {loading && data.rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500">Loading top aspirants…</div>
        ) : data.rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500">
            No study sessions logged yet for this period. Be the first to break a sweat — open the Timer.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-800" data-testid="leaderboard-list">
            {data.rows.map((r) => (
              <li key={r.user_id} className={`flex items-center gap-3 px-4 py-3 transition hover:bg-zinc-50 dark:hover:bg-zinc-800/40 ${myRowIndex === r.rank - 1 ? "bg-emerald-50/50 dark:bg-emerald-500/5" : ""}`} data-testid={`leaderboard-row-${r.rank}`}>
                <div className="grid w-9 shrink-0 place-items-center"><RankMedal rank={r.rank}/></div>
                <Avatar src={r.picture} name={r.name}/>
                <Link
                  to={`/u/${r.username}`}
                  className="min-w-0 flex-1 truncate"
                  data-testid={`leaderboard-link-${r.username}`}
                >
                  <div className="flex items-center gap-1.5 truncate font-medium">
                    {r.name}
                    {r.user_id === user?.user_id && (
                      <span className="rounded-full bg-emerald-500/15 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-emerald-600">You</span>
                    )}
                    {/* Tier badge — same source as Header / Profile / Community
                        (`public_premium_state` server-side). Hidden for free
                        users (TierBadge internally returns null when tier is
                        neither "premium" nor "trial"), matching the rest of
                        the app so the leaderboard doesn't look busy. */}
                    <TierBadge tier={r.tier} size="xs" showFree testid={`leaderboard-row-${r.rank}-tier`} />
                  </div>
                  <div className="truncate text-xs text-zinc-500">@{r.username}</div>
                </Link>
                <div className="text-right">
                  <div className="text-sm font-semibold tabular-nums">{fmtDuration(r.total_seconds)}</div>
                  <div className="mt-0.5 flex items-center justify-end gap-2 text-xs text-zinc-500">
                    <span className="inline-flex items-center gap-1" data-testid={`leaderboard-row-${r.rank}-streak`}>
                      <Flame size={11} className="text-amber-500"/>
                      {r.streak_days > 0 ? `${r.streak_days}d` : "—"}
                    </span>
                    {effectivePremium && (
                      <span className="inline-flex items-center gap-1" title="Achievements"><Award size={11} className="text-emerald-500"/>{r.achievements_count || 0}</span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Free-tier upgrade nudge — only when there are MORE rows we're hiding */}
      {!data.is_premium && data.rows.length >= data.free_preview_size && data.total_participants > data.free_preview_size && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-50 p-4 dark:bg-amber-500/10" data-testid="leaderboard-upgrade-nudge">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
            <Lock size={18}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">See the full leaderboard with Premium</div>
            <div className="text-xs text-zinc-600 dark:text-zinc-400">Unlock all {data.total_participants}+ rankings, weekly &amp; all-time stats, and your achievement badges.</div>
            <div className="mt-1 text-xs font-medium text-amber-600 dark:text-amber-400" data-testid="leaderboard-upgrade-pricing">₹49/month · ₹299/year</div>
          </div>
          <Link to="/premium">
            <Button className="bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="leaderboard-upgrade-btn">
              Upgrade
            </Button>
          </Link>
        </div>
      )}

      <div className="rounded-xl bg-zinc-50 px-4 py-3 text-xs text-zinc-500 dark:bg-zinc-900/60">
        <Crown size={12} className="mr-1.5 inline -mt-0.5 text-amber-500"/>
        Rankings are based on total study time logged via Syllox Timer. Cached for 5 min to keep the app snappy. Last updated{" "}
        {meta.computed_at ? new Date(meta.computed_at).toLocaleTimeString() : "—"}.
      </div>
    </div>
  );
}
